/* ==========================================================================
   NASH MARKET — L'EN-TÊTE
   Réf. direction artistique §5.1 à §5.5, §7.7, §8.5.

   Ce fichier ne touche à rien de ce que gère theme.js : il n'écoute aucun
   attribut `data-drawer-*` ni `data-backdrop`, sinon les deux moteurs de
   panneaux se répondraient l'un l'autre. Les seuls points de contact sont
   `[data-cart-count]` (lu, jamais écrit) et `[data-cart-anchor]` (posé côté
   Liquid pour la vignette volante).
   ========================================================================== */
(function () {
  'use strict';

  var entete = document.querySelector('[data-nm-entete]');
  if (!entete) return;

  var corps  = document.body;
  var racine = document.documentElement;

  /* Le mode éditeur de thème doit rester inerte : une barre qui se rétracte
     rend la sélection de sections impossible dans l'aperçu Shopify. */
  var modeEditeur = !!(window.Shopify && window.Shopify.designMode);

  var mouvementReduit = function () {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
           racine.getAttribute('data-nm-motion') === 'off';
  };

  /* ── Textes traduits : jamais de chaîne écrite ici ─────────────────────── */
  var T = {};
  try {
    var bloc = document.querySelector('[data-nm-entete-i18n]');
    if (bloc) T = JSON.parse(bloc.textContent);
  } catch (e) { T = {}; }

  var nombre = function (modele, n) {
    return String(modele || '').replace('__n__', String(n));
  };

  /* La durée d'un panneau est un JETON, pas un nombre : si --nm-d-panneau
     change dans nash-tokens.css, un 500 écrit ici désynchroniserait la remise
     à `hidden` de la glissière CSS. On lit donc la valeur calculée, une seule
     fois, avec un repli si la propriété n'a pas encore été résolue. */
  var dureePanneauCache = null;
  function dureePanneau() {
    if (dureePanneauCache !== null) return dureePanneauCache;
    var v = getComputedStyle(racine).getPropertyValue('--nm-d-panneau').trim();
    var n = parseFloat(v);
    if (!isFinite(n) || n <= 0) n = 0.5;
    dureePanneauCache = /ms\s*$/.test(v) ? n : n * 1000;
    return dureePanneauCache;
  }

  /* ══════════════════════════════════════════════════════════════════════
     1. LA HAUTEUR RÉELLE
     Sur Shopify la hauteur dépend du logo choisi par le marchand, et
     l'éditeur ajoute sa propre barre. Sans mesure, le calc(100svh - …) du
     hero est décalé de 20 à 40 px et la première cellule passe sous le pli.
     ═════════════════════════════════════════════════════════════════════ */
  function poserHauteur(h) {
    racine.style.setProperty('--nm-header-h', Math.round(h) + 'px');
  }
  if ('ResizeObserver' in window) {
    new ResizeObserver(function (entrees) {
      /* borderBoxSize plutôt que contentRect : la bordure basse d'un pixel et
         le padding de safe-area font partie de la place occupée. */
      var e = entrees[0];
      var h = e.borderBoxSize && e.borderBoxSize[0]
            ? e.borderBoxSize[0].blockSize
            : e.target.offsetHeight;
      poserHauteur(h);
    }).observe(entete);
  } else {
    poserHauteur(entete.offsetHeight);
    window.addEventListener('resize', function () { poserHauteur(entete.offsetHeight); }, { passive: true });
  }

  /* ══════════════════════════════════════════════════════════════════════
     2. LE VERROU DE DÉFILEMENT
     `position:fixed` sur le corps : sur iOS, `overflow:hidden` ne suffit pas,
     la page continue de défiler derrière le panneau. On mémorise et on
     restaure la position, sinon la fermeture renvoie en haut de page.
     ═════════════════════════════════════════════════════════════════════ */
  var yMemorise = 0;
  var verrous = 0;

  function verrouiller() {
    if (verrous++ > 0) return;
    yMemorise = window.scrollY || window.pageYOffset || 0;
    corps.style.top = (-yMemorise) + 'px';
    corps.classList.add('nm-verrou');
  }
  function deverrouiller() {
    if (verrous === 0) return;
    if (--verrous > 0) return;
    corps.classList.remove('nm-verrou');
    corps.style.top = '';
    window.scrollTo(0, yMemorise);
  }

  /* ══════════════════════════════════════════════════════════════════════
     3. LA RÉTRACTION AU DÉFILEMENT
     ═════════════════════════════════════════════════════════════════════ */
  var SEUIL = 8;        /* ignore le rebond élastique d'iOS */
  var DECLENCHE = 120;  /* on ne masque rien tant qu'on est près du haut */
  var yPrec = window.scrollY || 0;
  var enAttente = false;

  function panneauOuvert() {
    return corps.classList.contains('nm-menu') ||
           corps.classList.contains('nm-recherche');
  }
  function montrerEntete() { corps.classList.remove('nm-header-cache'); }

  function surDefilement() {
    enAttente = false;
    if (modeEditeur) return;
    if (panneauOuvert()) { montrerEntete(); return; }

    var y = window.scrollY || 0;
    if (Math.abs(y - yPrec) < SEUIL) return;
    if (y > yPrec && y > DECLENCHE) corps.classList.add('nm-header-cache');
    else montrerEntete();
    yPrec = y;
  }

  if (!modeEditeur) {
    window.addEventListener('scroll', function () {
      if (enAttente) return;
      enAttente = true;
      requestAnimationFrame(surDefilement);
    }, { passive: true });

    /* L'en-tête remonte au focus clavier d'un de ses éléments : sans cela, la
       tabulation atteint des liens situés hors de l'écran. */
    entete.addEventListener('focusin', montrerEntete);
  }

  /* ══════════════════════════════════════════════════════════════════════
     4. LE MÉGAMENU
     Le survol et le focus sont gérés en CSS. Le JS ne sert qu'aux cas que le
     CSS ne peut pas voir : le pointeur grossier (un iPad en paysage fait
     1024 px de large et n'a pourtant pas de survol) et la touche Échap.
     ═════════════════════════════════════════════════════════════════════ */
  var sansSurvol = window.matchMedia('(hover: none)');
  var dds = [].slice.call(entete.querySelectorAll('[data-nm-dd]'));

  function fermerDds(sauf) {
    dds.forEach(function (dd) {
      if (dd === sauf) return;
      dd.classList.remove('is-ouvert');
      var b = dd.querySelector('[data-nm-dd-bouton]');
      if (b) b.setAttribute('aria-expanded', 'false');
    });
  }

  dds.forEach(function (dd) {
    var bouton = dd.querySelector('[data-nm-dd-bouton]');
    if (!bouton) return;

    bouton.addEventListener('click', function (ev) {
      if (!sansSurvol.matches) return;   /* à la souris, le lien navigue */
      if (dd.classList.contains('is-ouvert')) return;  /* second appui : on suit le lien */
      ev.preventDefault();
      fermerDds(dd);
      dd.classList.add('is-ouvert');
      bouton.setAttribute('aria-expanded', 'true');
    });

    if (window.matchMedia('(hover: hover)').matches) {
      dd.addEventListener('mouseenter', function () { bouton.setAttribute('aria-expanded', 'true'); });
      dd.addEventListener('mouseleave', function () { bouton.setAttribute('aria-expanded', 'false'); });
    }
  });

  document.addEventListener('click', function (ev) {
    if (!ev.target.closest('[data-nm-dd]')) fermerDds(null);
  });

  /* ══════════════════════════════════════════════════════════════════════
     5. LE SÉLECTEUR DE LANGUE
     Le <details> natif porte le clavier et fonctionne sans JS. Il lui manque
     deux choses que les navigateurs ne font pas : la fermeture sur Échap et
     la fermeture au clic extérieur.
     ═════════════════════════════════════════════════════════════════════ */
  var langs = [].slice.call(document.querySelectorAll('[data-nm-lang]'));
  document.addEventListener('click', function (ev) {
    langs.forEach(function (d) {
      if (d.open && !d.contains(ev.target)) d.open = false;
    });
  });

  /* ══════════════════════════════════════════════════════════════════════
     6. LE TIROIR MOBILE
     ═════════════════════════════════════════════════════════════════════ */
  var tiroir = document.querySelector('[data-nm-tiroir]');
  var voile  = document.querySelector('[data-nm-voile]');
  var ouvreursMenu = [].slice.call(document.querySelectorAll('[data-nm-menu-ouvrir]'));
  var dernierFocus = null;
  /* Le tiroir n'est remis à `hidden` qu'une fois sa glissière terminée. Si on
     le rouvre entre-temps, le minuteur de la fermeture précédente le
     rehiderait EN COURS d'ouverture : le menu semblerait alors mort au second
     appui. D'où la référence gardée, annulée à chaque ouverture. */
  var minuteurTiroir = null;

  var FOCUSABLES = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),' +
                   'textarea:not([disabled]),summary,[tabindex]:not([tabindex="-1"])';

  function ouvrirMenu() {
    if (!tiroir) return;
    fermerRecherche(true);
    clearTimeout(minuteurTiroir);
    dernierFocus = document.activeElement;
    tiroir.hidden = false;
    if (voile) voile.hidden = false;
    verrouiller();
    requestAnimationFrame(function () {
      corps.classList.add('nm-menu');
      montrerEntete();
    });
    ouvreursMenu.forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
    setTimeout(function () {
      var cible = tiroir.querySelector('[data-nm-menu-fermer]') || tiroir.querySelector(FOCUSABLES);
      if (cible) cible.focus();
    }, 60);
  }

  function fermerMenu(sansFocus) {
    if (!tiroir || !corps.classList.contains('nm-menu')) return;
    corps.classList.remove('nm-menu');
    ouvreursMenu.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    deverrouiller();
    var fin = function () {
      tiroir.hidden = true;
      if (voile) voile.hidden = true;
    };
    clearTimeout(minuteurTiroir);
    if (mouvementReduit()) fin(); else minuteurTiroir = setTimeout(fin, dureePanneau());
    if (!sansFocus && dernierFocus && dernierFocus.focus) dernierFocus.focus();
  }

  ouvreursMenu.forEach(function (b) {
    b.addEventListener('click', function (ev) { ev.preventDefault(); ouvrirMenu(); });
  });
  document.addEventListener('click', function (ev) {
    if (ev.target.closest('[data-nm-menu-fermer]') || ev.target.closest('[data-nm-voile]')) {
      ev.preventDefault();
      fermerMenu();
    }
  });

  /* ══════════════════════════════════════════════════════════════════════
     7. LE RIDEAU DE RECHERCHE
     ═════════════════════════════════════════════════════════════════════ */
  var rech        = document.querySelector('[data-nm-rech]');
  var champ       = document.querySelector('[data-nm-rech-champ]');
  var compteur    = document.querySelector('[data-nm-rech-compteur]');
  var zoneRes     = document.querySelector('[data-nm-rech-res]');
  var zoneVide    = document.querySelector('[data-nm-rech-vide]');
  var lienTous    = document.querySelector('[data-nm-rech-tous]');
  var dym         = document.querySelector('[data-nm-rech-dym]');
  var dymBouton   = document.querySelector('[data-nm-rech-dym-btn]');
  var ouvreursRech = [].slice.call(document.querySelectorAll('[data-nm-rech-ouvrir]'));
  var focusAvantRech = null;

  function ouvrirRecherche() {
    if (!rech) return;
    fermerMenu(true);
    focusAvantRech = document.activeElement;
    verrouiller();
    corps.classList.add('nm-recherche');
    montrerEntete();
    ouvreursRech.forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
    chargerIndex();
    /* Le focus tombe à mi-course du rideau : plus tôt, iOS ouvre le clavier
       par-dessus une animation en cours et la saccade ; plus tard, on perd la
       première frappe. La moitié de --nm-d-panneau, jamais un nombre écrit. */
    setTimeout(function () { if (champ) champ.focus(); },
               mouvementReduit() ? 0 : Math.round(dureePanneau() / 2));
  }

  function fermerRecherche(sansFocus) {
    if (!rech || !corps.classList.contains('nm-recherche')) return;
    corps.classList.remove('nm-recherche');
    ouvreursRech.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    deverrouiller();
    if (!sansFocus && focusAvantRech && focusAvantRech.focus) focusAvantRech.focus();
  }

  ouvreursRech.forEach(function (b) {
    b.addEventListener('click', function (ev) { ev.preventDefault(); ouvrirRecherche(); });
  });
  document.addEventListener('click', function (ev) {
    if (ev.target.closest('[data-nm-rech-fermer]')) { ev.preventDefault(); fermerRecherche(); }
  });

  /* ── Échap et piège de focus, communs aux deux panneaux ────────────────── */
  document.addEventListener('keydown', function (ev) {
    if (ev.key === 'Escape') {
      var ouvert = false;
      langs.forEach(function (d) { if (d.open) { d.open = false; ouvert = true; } });
      if (dds.some(function (dd) { return dd.classList.contains('is-ouvert'); })) {
        fermerDds(null); ouvert = true;
      }
      if (corps.classList.contains('nm-recherche')) { fermerRecherche(); ouvert = true; }
      else if (corps.classList.contains('nm-menu'))  { fermerMenu(); ouvert = true; }
      if (ouvert) ev.stopPropagation();
      return;
    }
    if (ev.key !== 'Tab') return;

    var panneau = corps.classList.contains('nm-recherche') ? rech
                : corps.classList.contains('nm-menu') ? tiroir : null;
    if (!panneau) return;
    var items = [].slice.call(panneau.querySelectorAll(FOCUSABLES))
                 .filter(function (el) { return el.offsetParent !== null && !el.hasAttribute('hidden'); });
    if (!items.length) return;
    var premier = items[0], dernier = items[items.length - 1];
    if (ev.shiftKey && document.activeElement === premier) { ev.preventDefault(); dernier.focus(); }
    else if (!ev.shiftKey && document.activeElement === dernier) { ev.preventDefault(); premier.focus(); }
  });

  /* ══════════════════════════════════════════════════════════════════════
     8. LE MOTEUR DE RECHERCHE
     Deux sources, dans cet ordre :
     a) l'index local pré-généré assets/nm-index.<locale>.json — il porte les
        translittérations (« pelmeni » / « пельмени » / « пельмені »), donne un
        total EXACT et répond sans réseau ;
     b) à défaut, la recherche prédictive Shopify, qui plafonne à dix
        résultats et ne connaît qu'une seule graphie.
     L'index n'est chargé qu'au PREMIER focus du champ, jamais au chargement
     de la page : ~35 Ko en gzip qu'on ne fait pas payer à qui ne cherche pas.
     ═════════════════════════════════════════════════════════════════════ */
  var index = null;
  var indexDemande = false;

  function chargerIndex() {
    if (indexDemande || !T.urlIndex) return;
    indexDemande = true;
    fetch(T.urlIndex, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        /* On accepte les deux formes plausibles du fichier : un tableau, ou un
           objet { produits: [...] }. Le script de synchronisation du catalogue
           n'appartient pas au thème, autant ne pas en dépendre. */
        if (!d) return;
        index = Array.isArray(d) ? d : (d.produits || d.products || null);
      })
      .catch(function () { index = null; });
  }
  if (champ) champ.addEventListener('focus', chargerIndex, { once: false });

  function prix(v) {
    var n;
    if (typeof v === 'number') n = v / 100;
    else if (typeof v === 'string' && /[.,]/.test(v)) n = parseFloat(v.replace(',', '.'));
    else if (typeof v === 'string') n = parseInt(v, 10) / 100;
    else return '';
    if (!isFinite(n)) return '';
    try {
      return new Intl.NumberFormat(T.locale || undefined,
        { style: 'currency', currency: T.devise || 'EUR' }).format(n);
    } catch (e) { return n.toFixed(2); }
  }

  function echapper(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Distance de Levenshtein bornée : au-delà de 2 on abandonne, ce qui évite
     de dérouler la matrice complète sur 697 titres à chaque frappe. */
  function distance(a, b, max) {
    a = a.toLowerCase(); b = b.toLowerCase();
    if (Math.abs(a.length - b.length) > max) return max + 1;
    var prec = [], cour = [], i, j;
    for (j = 0; j <= b.length; j++) prec[j] = j;
    for (i = 1; i <= a.length; i++) {
      cour[0] = i;
      var mini = cour[0];
      for (j = 1; j <= b.length; j++) {
        cour[j] = Math.min(prec[j] + 1, cour[j - 1] + 1,
                           prec[j - 1] + (a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1));
        if (cour[j] < mini) mini = cour[j];
      }
      if (mini > max) return max + 1;
      prec = cour.slice();
    }
    return prec[b.length];
  }

  function chercherIndex(q) {
    var m = q.toLowerCase().trim();
    var exacts = [];
    for (var i = 0; i < index.length; i++) {
      var p = index[i];
      var champs = (p.titre || p.title || '') + ' ' + (p.search_terms || p.termes || []).join(' ');
      if (champs.toLowerCase().indexOf(m) !== -1) exacts.push(p);
    }
    return exacts;
  }

  function suggestionIndex(q) {
    var m = q.toLowerCase().trim();
    if (m.length < 4) return null;
    var meilleur = null, meilleureD = 3;
    for (var i = 0; i < index.length && i < 2000; i++) {
      var termes = (index[i].search_terms || index[i].termes || []);
      for (var j = 0; j < termes.length; j++) {
        var d = distance(m, String(termes[j]), 2);
        if (d < meilleureD) { meilleureD = d; meilleur = termes[j]; }
      }
    }
    return meilleureD <= 2 ? meilleur : null;
  }

  function carte(p) {
    var url   = p.url || p.handle_url || ('/products/' + (p.handle || ''));
    var titre = p.titre || p.title || '';
    var img   = p.image || p.featured_image || (p.featured_image && p.featured_image.url) || '';
    if (img && typeof img === 'object') img = img.url || '';
    var p2 = p.prix != null ? p.prix : p.price;
    return '<a class="nm-rech__cell" href="' + echapper(url) + '">' +
             '<span class="nm-rech__puits">' +
               (img ? '<img src="' + echapper(img) + '" alt="" loading="lazy" width="180" height="180">' : '') +
             '</span>' +
             '<span class="nm-rech__texte">' +
               '<span class="nm-rech__nom">' + echapper(titre) + '</span>' +
               '<span class="nm-rech__prix nm-num">' + echapper(prix(p2)) + '</span>' +
             '</span>' +
           '</a>';
  }

  function afficher(produits, total, q, suggestion) {
    if (!zoneRes) return;
    zoneRes.innerHTML = produits.map(carte).join('');
    if (zoneVide) zoneVide.hidden = q.length > 0;

    if (compteur) {
      compteur.textContent = !q ? ''
        : total === 0 ? (T.aucunResultat || '')
        : total === 1 ? (T.resultatUn || '')
        : nombre(T.resultats, total);
    }
    if (lienTous) {
      lienTous.hidden = !(q && total > produits.length);
      lienTous.href = (T.urlRecherche || '/search') + '?q=' + encodeURIComponent(q) + '&type=product' + '&filter.v.availability=1';
    }
    if (dym && dymBouton) {
      if (suggestion && total === 0) {
        dymBouton.textContent = suggestion;
        dym.hidden = false;
      } else {
        dym.hidden = true;
      }
    }
  }

  var minuteur = null;
  var enCours = null;

  function interroger(q) {
    if (!q) { afficher([], 0, '', null); return; }

    if (index) {
      var trouves = chercherIndex(q);
      afficher(trouves.slice(0, 12), trouves.length, q,
               trouves.length ? null : suggestionIndex(q));
      return;
    }

    /* Repli prédictif Shopify. `resources[type]=product,query` sert deux fins :
       les produits, et les requêtes voisines qui alimentent « vouliez-vous
       dire » — la seule source de correction dont on dispose sans l'index. */
    if (enCours) enCours.abort();
    enCours = ('AbortController' in window) ? new AbortController() : null;
    /* .json obligatoire : sans lui, Shopify rend du HTML et r.json() lève —
       la recherche vivante répondait toujours « aucun résultat » (relevé par
       la relecture). */
    var base = (T.urlPredictif || '/search/suggest').replace(/\.json$/, '') + '.json';
    var url = base +
              '?q=' + encodeURIComponent(q) +
              '&resources[type]=product,query&resources[limit]=8' +
              '&resources[options][unavailable_products]=hide';

    if (compteur) compteur.textContent = T.chargement || '';

    fetch(url, {
      headers: { Accept: 'application/json' },
      signal: enCours ? enCours.signal : undefined
    })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        var res = (d && d.resources && d.resources.results) || {};
        var produits = res.products || [];
        var requetes = res.queries || [];
        var suggestion = requetes.length && requetes[0].text !== q ? requetes[0].text : null;
        /* La prédictive ne renvoie AUCUN total : on annonce ce qu'on montre, et
           « voir tous les résultats » prend le relais dès qu'on est au plafond. */
        afficher(produits, produits.length, q, suggestion);
        if (lienTous) lienTous.hidden = !q;
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        afficher([], 0, q, null);
      });
  }

  if (champ) {
    champ.addEventListener('input', function () {
      var q = champ.value.trim();
      clearTimeout(minuteur);
      /* 180 ms : le compteur annonce APRÈS stabilisation de la frappe, jamais
         à chaque caractère — sinon aria-live bavarde en continu. */
      minuteur = setTimeout(function () { interroger(q); }, 180);
    });
  }

  document.addEventListener('click', function (ev) {
    var puce = ev.target.closest('[data-nm-puce]');
    if (puce && champ) {
      ev.preventDefault();
      champ.value = puce.getAttribute('data-nm-puce');
      champ.focus();
      interroger(champ.value.trim());
      return;
    }
    if (dymBouton && ev.target.closest('[data-nm-rech-dym-btn]') && champ) {
      ev.preventDefault();
      champ.value = dymBouton.textContent;
      champ.focus();
      interroger(champ.value.trim());
    }
  });

  /* ══════════════════════════════════════════════════════════════════════
     9. LE LIBELLÉ DU PANIER
     theme.js met à jour le chiffre de la pastille. Le libellé lu par les
     lecteurs d'écran doit suivre, sinon il annonce éternellement « 0 article ».
     ═════════════════════════════════════════════════════════════════════ */
  var pastille = entete.querySelector('[data-cart-count]');
  var libelle  = entete.querySelector('[data-nm-panier-libelle]');
  if (pastille && libelle && 'MutationObserver' in window) {
    new MutationObserver(function () {
      var n = parseInt(pastille.textContent, 10) || 0;
      pastille.setAttribute('data-count', String(n));
      libelle.textContent = nombre(T.panierCompte, n);
    }).observe(pastille, { childList: true, characterData: true, subtree: true });
  }
})();
